<div class="container">
  <h2>Asset Pickup Queue</h2>

  <!-- Search Bar -->
  <mat-form-field appearance="outline" class="search-field">
    <mat-label>Search</mat-label>
    <input matInput (keyup)="applyFilter($event)" placeholder="Search assets, drivers, sites..." #input />
    <mat-icon matSuffix>search</mat-icon>
  </mat-form-field>

  <!-- Loading Overlay -->
  <div *ngIf="isLoading" class="loading-overlay">
    <mat-spinner diameter="50"></mat-spinner>
  </div>

  <!-- Desktop Table View (hidden on mobile) -->
  <div class="mat-elevation-z8 table-container desktop-only">
    <table mat-table [dataSource]="dataSource" matSort>
      <!-- Driver Name Column -->
      <ng-container matColumnDef="driverName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Driver Name</th>
        <td mat-cell *matCellDef="let row">{{ row.driverName }}</td>
      </ng-container>

      <ng-container matColumnDef="phoneNumber">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Phone Number</th>
        <td mat-cell *matCellDef="let row">{{ row.phoneNumber }}</td>
      </ng-container>

      <ng-container matColumnDef="companyName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Company Name</th>
        <td mat-cell *matCellDef="let row">{{ row.companyName }}</td>
      </ng-container>

      <ng-container matColumnDef="scheduledAt">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Pickup Date</th>
        <td mat-cell *matCellDef="let row">{{ row.scheduledAt | standardDate }}</td>
      </ng-container>

      <!-- Asset Name Column -->
      <ng-container matColumnDef="assetName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Asset Name</th>
        <td mat-cell *matCellDef="let row">{{ row.assetName }}</td>
      </ng-container>

      <!-- Pickup Site Name Column -->
      <ng-container matColumnDef="pickupSiteName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Pickup Site Name</th>
        <td mat-cell *matCellDef="let row">{{ row.pickupSiteName }}</td>
      </ng-container>

      <!-- Sufficient Escrow Column -->
      <ng-container matColumnDef="sufficientEscrow">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Sufficient Escrow</th>
        <td mat-cell *matCellDef="let row">
          <mat-icon *ngIf="row.sufficientEscrow" class="success-icon">check_circle</mat-icon>
          <mat-icon *ngIf="!row.sufficientEscrow" class="error-icon">cancel</mat-icon>
        </td>
      </ng-container>

      <!-- Status Column -->
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
        <td mat-cell *matCellDef="let row">
          <span class="status-badge" *ngIf="row.archivedAt"> CANCELLED </span>
          <span class="status-badge" *ngIf="!row.archivedAt && row.scheduledAt && !row.completedAt"> SCHEDULED </span>
          <span class="status-badge" *ngIf="!row.archivedAt && row.scheduledAt && row.completedAt"> COMPLETED </span>
        </td>
      </ng-container>

      <!-- Actions Column -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef>Actions</th>
        <td mat-cell *matCellDef="let row">
          <button mat-icon-button [matMenuTriggerFor]="menu" aria-label="Options">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #menu="matMenu">
            <button mat-menu-item (click)="assignAsset(row)">
              <mat-icon>edit</mat-icon>
              <span>Edit Asset</span>
            </button>
            <button mat-menu-item (click)="cancelPickup(row)">
              <mat-icon>cancel</mat-icon>
              <span>Cancel Pickup</span>
            </button>
          </mat-menu>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns; trackBy: trackByFn"></tr>

      <!-- Row shown when there is no matching data. -->
      <tr class="mat-row" *matNoDataRow>
        <td class="mat-cell" colspan="4">No data matching the filter "{{ input.value }}"</td>
      </tr>
    </table>

    <!-- Pagination -->
    <mat-paginator
      [pageSizeOptions]="[5, 10, 25, 100]"
      [pageSize]="pageSize"
      [length]="totalItems"
      [pageIndex]="currentPage"
      aria-label="Select page of assets"
    >
    </mat-paginator>
  </div>

  <!-- Mobile Card View (hidden on desktop) -->
  <div class="mobile-card-container mobile-only">
    <!-- No Data Message -->
    <div *ngIf="dataSource.data.length === 0" class="no-data-message">
      <mat-icon>search_off</mat-icon>
      <p>No data matching the filter "{{ input.value }}"</p>
    </div>

    <!-- Card for each row -->
    <mat-card *ngFor="let row of dataSource.data; trackBy: trackByFn" class="mobile-card">
      <mat-card-header>
        <mat-card-title>
          {{ row.driverName }}
        </mat-card-title>
        <mat-card-subtitle>
          {{ row.companyName }}
        </mat-card-subtitle>
        <div class="card-status">
          <span class="status-badge" *ngIf="row.archivedAt"> CANCELLED </span>
          <span class="status-badge" *ngIf="!row.archivedAt && row.scheduledAt && !row.completedAt"> SCHEDULED </span>
          <span class="status-badge" *ngIf="!row.archivedAt && row.scheduledAt && row.completedAt"> COMPLETED </span>
        </div>
      </mat-card-header>

      <mat-card-content>
        <div class="info-row">
          <span class="info-label">Phone:</span>
          <span class="info-value">
            <a [href]="'tel:' + row.phoneNumber">{{ row.phoneNumber }}</a>
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">Asset:</span>
          <span class="info-value">{{ row.assetName }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Pickup Site:</span>
          <span class="info-value">{{ row.pickupSiteName }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Escrow:</span>
          <span class="info-value">
            <mat-icon *ngIf="row.sufficientEscrow" class="success-icon">check_circle</mat-icon>
            <mat-icon *ngIf="!row.sufficientEscrow" class="error-icon">cancel</mat-icon>
          </span>
        </div>
      </mat-card-content>

      <mat-card-actions>
        <button mat-button color="primary" (click)="assignAsset(row)"><mat-icon>edit</mat-icon> Edit</button>
        <button mat-button color="warn" (click)="cancelPickup(row)"><mat-icon>cancel</mat-icon> Cancel</button>
      </mat-card-actions>
    </mat-card>

    <!-- Mobile Pagination -->
    <mat-paginator
      class="mobile-paginator"
      [pageSizeOptions]="[5, 10, 25]"
      [pageSize]="pageSize"
      [length]="totalItems"
      [pageIndex]="currentPage"
      aria-label="Select page of assets"
    >
    </mat-paginator>
  </div>
</div>
